/**
 * =============================================
 *  SHOPIFY FREE GIFT — DRAWER CART INTEGRATION
 * =============================================
 *  HOW IT WORKS:
 *  - Hooks into native Shopify cart events
 *  - If cart subtotal >= THRESHOLD → adds gift
 *  - If cart drops below threshold  → removes gift
 *  - Never double-adds the gift product
 *  - After adding/removing, re-renders the drawer + cart count
 *    the SAME WAY this theme's own theme.js does (sections=
 *    cart-drawer,header), because this theme does not dispatch
 *    any custom cart events for us to listen to.
 *
 *  PERF NOTE (this revision):
 *  Every cart/change.js and cart/update.js response already contains the
 *  full cart object (items[], item_count, etc). Previously this script threw
 *  that away and fired its own separate `/cart.js` GET on every single cart
 *  mutation, then — if it needed to add/remove the gift — fired ANOTHER full
 *  drawer/header re-render on top of the one theme.js already does for the
 *  same interaction. That's why quantity +/- and remove in the drawer could
 *  feel slow/double-rendered even after theme.js's own round-trip was fixed.
 *  Now: reuse the cart data already returned by change.js/update.js (skip
 *  the extra /cart.js call), and when the gift itself needs to change, ask
 *  Shopify to return the re-rendered sections in that SAME add/remove call
 *  instead of a separate refreshDrawerCart() fetch. add.js (single-item)
 *  responses don't include the full items[] list, so a cart.js fetch is
 *  still used there — that path is unchanged.
 *
 *  SETUP:
 *  1. Replace GIFT_VARIANT_ID with your gift product's variant ID
 *  2. Replace CART_THRESHOLD with your desired minimum (default: 500)
 *  3. Paste this file in: Assets/free-gift-cart.js
 *  4. Include it in theme.liquid (see bottom of this file)
 * =============================================
 */

(function () {
  'use strict';

  // ─── CONFIG ──────────────────────────────────────────────────────────────────
  const GIFT_VARIANT_ID = 57231685255433;   // ← Replace with your gift variant ID
  const CART_THRESHOLD  = 500;          // ← Minimum cart value (in your store currency)
  const GIFT_KEY        = 'free_gift';  // Internal marker — do not change
  // ─────────────────────────────────────────────────────────────────────────────

  let isBusy = false; // Prevent concurrent calls

  function themeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /**
   * Swap in freshly rendered cart-drawer/header markup, exactly like this
   * theme's own theme.js does after a cart mutation.
   */
  function applySections(sections) {
    if (!sections) return;
    const tmp = document.createElement('div');
    if (sections['cart-drawer']) {
      tmp.innerHTML = sections['cart-drawer'];
      const fresh = tmp.querySelector('#cart-drawer');
      const cur = document.getElementById('cart-drawer');
      if (fresh && cur) {
        const wasOpen = cur.hasAttribute('data-open');
        cur.replaceWith(fresh);
        if (wasOpen) fresh.setAttribute('data-open', '');
      }
    }
    if (sections['header']) {
      tmp.innerHTML = sections['header'];
      const freshCount = tmp.querySelector('[data-cart-count]');
      const liveCount = document.querySelector('[data-cart-count]');
      if (freshCount && liveCount) liveCount.textContent = freshCount.textContent;
    }
  }

  /**
   * Fetch the current cart state from Shopify.
   * Only used as a fallback when we don't already have the cart's items[]
   * from the mutation that triggered us (see attachListeners below).
   * @returns {Promise<Object>} Cart object
   */
  async function getCart() {
    const res = await fetch(themeRoot() + 'cart.js', {
      headers: { 'Content-Type': 'application/json' }
    });
    return res.json();
  }

  /**
   * Add the gift product to cart (quantity: 1, price: 0 via discount or note).
   * Requests the re-rendered sections inline so we don't need a second
   * drawer-refresh fetch afterward.
   */
  async function addGift() {
    const res = await fetch(themeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: GIFT_VARIANT_ID,
          quantity: 1,
          properties: {
            _gift: GIFT_KEY  // Hidden property to identify this line item
          }
        }],
        sections: 'cart-drawer,header',
        sections_url: window.location.pathname
      })
    });
    try { return await res.json(); } catch (e) { return null; }
  }

  /**
   * Remove the gift product from cart using its line key.
   * Requests the re-rendered sections inline (see addGift note above).
   * @param {string} lineKey - The cart line item key
   */
  async function removeGift(lineKey) {
    const res = await fetch(themeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0, sections: 'cart-drawer,header', sections_url: window.location.pathname })
    });
    try { return await res.json(); } catch (e) { return null; }
  }

  /**
   * Core logic: evaluate cart and add/remove gift accordingly.
   * @param {Object} [cartData] - Cart object already available from the
   *   mutation that triggered this call (avoids a redundant /cart.js fetch).
   *   Falls back to fetching /cart.js when not provided.
   */
  async function evaluateCart(cartData) {
    if (isBusy) return;
    isBusy = true;

    try {
      const cart = cartData || await getCart();

      // Find existing gift line (if any)
      const giftLine = cart.items.find(
        item =>
          item.variant_id === GIFT_VARIANT_ID &&
          item.properties &&
          item.properties._gift === GIFT_KEY
      );

      // Calculate subtotal excluding the gift itself (so gift doesn't inflate total)
      const subtotalWithoutGift = cart.items
        .filter(item => !(item.properties && item.properties._gift === GIFT_KEY))
        .reduce((sum, item) => sum + item.line_price, 0);

      const subtotalInCurrency = subtotalWithoutGift / 100; // Shopify stores in cents

      console.log(`[FreeGift] Cart subtotal: ${subtotalInCurrency} | Threshold: ${CART_THRESHOLD}`);

      let mutationResult = null;

      if (subtotalInCurrency >= CART_THRESHOLD) {
        if (!giftLine) {
          console.log('[FreeGift] Threshold reached — adding gift...');
          mutationResult = await addGift();
        } else {
          console.log('[FreeGift] Gift already in cart — skipping.');
        }
      } else {
        if (giftLine) {
          console.log('[FreeGift] Below threshold — removing gift...');
          mutationResult = await removeGift(giftLine.key);
        }
      }

      // Only re-render here if WE just changed the cart (added/removed the
      // gift). If we didn't touch the cart, whatever triggered evaluateCart()
      // (theme.js's own qty/add handlers) already rendered the correct state
      // itself — rendering again here would just be a second, redundant paint.
      if (mutationResult && mutationResult.sections) {
        applySections(mutationResult.sections);
      }
    } catch (err) {
      console.error('[FreeGift] Error evaluating cart:', err);
    } finally {
      isBusy = false;
    }
  }

  /**
   * Listen to all relevant Shopify cart mutation events.
   * Kept for forward-compatibility / other themes; this theme does not
   * currently dispatch any of these, so the fetch-monkeypatch below is what
   * actually catches the theme's own add-to-cart flow.
   */
  function attachListeners() {
    const CART_EVENTS = [
      'cart:updated',          // Generic theme event
      'cart:item-added',       // After add
      'cart:item-removed',     // After remove
      'shopify:cart:change',   // Native Shopify (where supported)
      'cart:refresh',          // This theme's toast script fires this (unused by theme.js, but harmless to listen for)
      'cart:build',
    ];

    CART_EVENTS.forEach(evt => {
      document.addEventListener(evt, function () { evaluateCart(); });
      window.addEventListener(evt, function () { evaluateCart(); });
    });

    // ── Intercept fetch calls to Shopify cart endpoints ──
    // This is the mechanism that actually catches this theme's own
    // add-to-cart flow (theme.js and md-cart-toast.js both call
    // fetch('.../cart/add.js') directly, with no event dispatched that we
    // can hook into).
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const result = await originalFetch.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      const isCartMutation =
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear');

      if (isCartMutation && !isBusy) {
        // cart/change.js, cart/update.js and cart/clear.js already return the
        // FULL cart object (items[], item_count, etc) in their response body.
        // Reuse that instead of firing a second /cart.js GET for every single
        // cart interaction. cart/add.js's single-item response does not
        // include items[], so that path still falls back to a fresh fetch.
        result.clone().json().then(function (data) {
          if (data && Array.isArray(data.items)) {
            evaluateCart(data);
          } else {
            evaluateCart();
          }
        }).catch(function () { evaluateCart(); });
      }

      return result;
    };

    console.log('[FreeGift] Cart listeners attached.');
  }

  // ─── INIT ─────────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    attachListeners();
    evaluateCart(); // Run once on page load (handles page refresh with items in cart)
  });

})();
